import React, { useState, useEffect } from 'react';
import { ExternalLink, BookOpen, FileText, Link as LinkIcon, Download, Search, Clock } from 'lucide-react';

const Card = ({ children, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-slate-200 ${className}`}>
        {children}
    </div>
);

export default function TeacherResources({ setTeacherViewMode }) {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchLinks = async () => {
            try {
                // Endpoint GVIZ for the Teacher Resources Spreadsheet
                const csvUrl = 'https://docs.google.com/spreadsheets/d/1kbJ-gtU-uAZjJYRH9_FlS742euZso3NxF6RV_Dr37zU/gviz/tq?tqx=out:csv';

                const response = await fetch(csvUrl);
                if (!response.ok) throw new Error('Falha ao carregar planilha');

                const text = await response.text();

                // Parse CSV
                const lines = text.split('\n');
                const dataLines = lines.slice(1).filter(line => line.trim() !== '');

                const fetchedLinks = dataLines.map(line => {
                    const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
                    const parts = matches.length >= 3 ? matches : line.split(',');

                    const clean = (str) => str ? str.replace(/^"|"$/g, '').trim() : '';

                    const name = clean(parts[0]);
                    const category = clean(parts[1]);
                    const url = clean(parts[2]);

                    if (!name) return null;

                    return { name, category, url };
                }).filter(item => item !== null);

                setLinks(fetchedLinks);
            } catch (error) {
                console.error("Erro ao buscar links:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLinks();
    }, []);

    const groupedLinks = links
        .filter(link => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
                link.name.toLowerCase().includes(term) ||
                link.category.toLowerCase().includes(term)
            );
        })
        .reduce((acc, link) => {
            if (!link.url || link.url === '#' || link.url === '') return acc;
            if (!acc[link.category]) acc[link.category] = [];
            acc[link.category].push(link);
            return acc;
        }, {});

    const categories = Object.keys(groupedLinks).sort();

    return (
        <div className="h-full bg-slate-100 p-6 overflow-y-auto">
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800">Links e Documentos</h1>
                        <p className="text-slate-500 mt-2">Acesso rápido a arquivos e plataformas da escola.</p>
                    </div>
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar recursos..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Funcionalidades Nativas */}
                        <Card className="p-6 border-indigo-100 bg-indigo-50/30">
                            <h2 className="text-xl font-bold text-indigo-900 mb-6 flex items-center gap-2 border-b border-indigo-100 pb-4">
                                <Clock className="text-indigo-500 w-6 h-6" />
                                Horários e Alocações
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <button
                                    onClick={() => setTeacherViewMode && setTeacherViewMode('ctime')}
                                    className="flex items-start gap-4 p-4 rounded-xl border border-indigo-200 bg-white hover:bg-indigo-50 hover:shadow-md hover:border-indigo-300 transition-all group duration-300 text-left w-full"
                                >
                                    <div className="p-2 bg-indigo-100 rounded-lg shadow-sm group-hover:bg-indigo-200 transition-colors">
                                        <Clock className="w-5 h-5 text-indigo-700" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-indigo-900 group-hover:text-indigo-700 truncate transition-colors">
                                            Community Time & Cycle
                                        </h3>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-indigo-500">
                                            <span>Consulte sua alocação semanal</span>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </Card>

                        {/* Recursos da Planilha */}
                        {categories.map(category => (
                            <Card key={category} className="p-6">
                                <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
                                    <BookOpen className="text-blue-500 w-6 h-6" />
                                    {category}
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedLinks[category].map((link, idx) => (
                                        <a
                                            key={idx}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-start gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-md hover:border-blue-200 transition-all group duration-300"
                                        >
                                            <div className="p-2 bg-white rounded-lg shadow-sm group-hover:bg-blue-50 transition-colors">
                                                <ExternalLink className="w-5 h-5 text-blue-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-slate-700 group-hover:text-blue-600 truncate transition-colors">
                                                    {link.name}
                                                </h3>
                                                <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                                                    <span>Clique para acessar</span>
                                                </div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </Card>
                        ))}

                        {categories.length === 0 && (
                            <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed border-slate-300">
                                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">Nenhum documento encontrado.</p>
                                <p className="text-sm">Os links aparecerão aqui assim que forem adicionados à planilha.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
